import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";
import { WEEKDAY_NAMES, recurrenceLabel, RECURRENCE_COLORS } from "../lib/recurrence";

const ORDINALS = [
  { v: 1, l: "First" },
  { v: 2, l: "Second" },
  { v: 3, l: "Third" },
  { v: 4, l: "Fourth" },
  { v: -1, l: "Last" },
];

const DEFAULT = {
  freq: "daily",
  interval: 1,
  by_weekdays: [],
  monthly_mode: "day_of_month",
  monthly_weekday_ordinal: 1,
  end: { type: "never" },
};

export default function CustomRecurrenceBuilder({ open, onClose, value, onSave }) {
  const [rule, setRule] = useState(DEFAULT);

  useEffect(() => {
    if (open) {
      setRule({
        ...DEFAULT,
        ...(value || {}),
        interval: value?.interval || 1,
        by_weekdays: value?.by_weekdays || [],
        end: value?.end || { type: "never" },
      });
    }
  }, [open, value]);

  const set = (k, v) => setRule((r) => ({ ...r, [k]: v }));
  const setEnd = (k, v) => setRule((r) => ({ ...r, end: { ...(r.end || {}), [k]: v } }));

  const unitLabel = {
    daily: rule.interval === 1 ? "day" : "days",
    weekly: rule.interval === 1 ? "week" : "weeks",
    monthly: rule.interval === 1 ? "month" : "months",
    yearly: rule.interval === 1 ? "year" : "years",
  }[rule.freq] || "";

  const toggleWeekday = (i) => {
    const current = rule.by_weekdays || [];
    const next = current.includes(i)
      ? current.filter((x) => x !== i)
      : [...current, i].sort();
    set("by_weekdays", next);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="custom-recurrence-modal" className="max-w-md p-0">
        <div
          className="px-6 pt-5 pb-4 border-b border-border"
          style={{ background: `linear-gradient(90deg, ${RECURRENCE_COLORS.custom}14, transparent 80%)` }}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Custom recurrence</DialogTitle>
            <DialogDescription className="text-xs">
              Build a precise repeat rule — frequency, interval, weekday selection, and end conditions.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Interval */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Repeats</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-sm">Every</span>
              <Input
                data-testid="custom-interval"
                type="number"
                min={1}
                max={365}
                value={rule.interval}
                onChange={(e) => set("interval", Math.max(1, parseInt(e.target.value || "1", 10)))}
                className="w-20 h-10"
              />
              <Select value={rule.freq} onValueChange={(v) => set("freq", v)}>
                <SelectTrigger className="flex-1" data-testid="custom-freq">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">{unitLabel === "day" || rule.freq === "daily" ? "days" : "days"}</SelectItem>
                  <SelectItem value="weekly">weeks</SelectItem>
                  <SelectItem value="monthly">months</SelectItem>
                  <SelectItem value="yearly">years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Weekly: day picker */}
          {rule.freq === "weekly" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Repeat on</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {WEEKDAY_NAMES.map((d, idx) => {
                  const selected = (rule.by_weekdays || []).includes(idx);
                  return (
                    <button
                      key={d}
                      type="button"
                      data-testid={`custom-weekday-${idx}`}
                      onClick={() => toggleWeekday(idx)}
                      style={
                        selected
                          ? {
                              backgroundColor: RECURRENCE_COLORS.custom,
                              color: "#0a0a0a",
                              borderColor: RECURRENCE_COLORS.custom,
                            }
                          : {}
                      }
                      className={cn(
                        "w-10 h-10 rounded-md border border-border text-xs font-medium transition-colors",
                        !selected && "hover:bg-secondary/60"
                      )}
                    >
                      {d.charAt(0)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Monthly: same-day vs n-th weekday */}
          {rule.freq === "monthly" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Monthly pattern</Label>
              <div className="mt-1.5 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="monthly-mode"
                    checked={rule.monthly_mode === "day_of_month"}
                    onChange={() => set("monthly_mode", "day_of_month")}
                    data-testid="monthly-mode-day"
                  />
                  Same day of the month
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="monthly-mode"
                    checked={rule.monthly_mode === "weekday_of_month"}
                    onChange={() => set("monthly_mode", "weekday_of_month")}
                    data-testid="monthly-mode-weekday"
                  />
                  N-th weekday of the month
                </label>
                {rule.monthly_mode === "weekday_of_month" && (
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <Select
                      value={String(rule.monthly_weekday_ordinal || 1)}
                      onValueChange={(v) => set("monthly_weekday_ordinal", parseInt(v, 10))}
                    >
                      <SelectTrigger data-testid="monthly-ordinal">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ORDINALS.map((o) => (
                          <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={String(rule.by_weekdays?.[0] ?? 0)}
                      onValueChange={(v) => set("by_weekdays", [parseInt(v, 10)])}
                    >
                      <SelectTrigger data-testid="monthly-weekday">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKDAY_NAMES.map((d, i) => (
                          <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* End condition */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ends</Label>
            <div className="mt-1.5 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="end-type"
                  checked={rule.end?.type === "never"}
                  onChange={() => setEnd("type", "never")}
                  data-testid="end-never"
                />
                Never
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="end-type"
                  checked={rule.end?.type === "count"}
                  onChange={() => setEnd("type", "count")}
                  data-testid="end-count"
                />
                After
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={rule.end?.count || 10}
                  onChange={(e) => setEnd("count", Math.max(1, parseInt(e.target.value || "1", 10)))}
                  onFocus={() => setEnd("type", "count")}
                  className="w-20 h-9"
                  data-testid="end-count-input"
                />
                occurrences
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="end-type"
                  checked={rule.end?.type === "until"}
                  onChange={() => setEnd("type", "until")}
                  data-testid="end-until"
                />
                On
                <Input
                  type="date"
                  value={rule.end?.until || ""}
                  onChange={(e) => setEnd("until", e.target.value)}
                  onFocus={() => setEnd("type", "until")}
                  className="w-44 h-9"
                  data-testid="end-until-input"
                />
              </label>
            </div>
          </div>

          {/* Preview */}
          <div
            className="rounded-md border px-3 py-2 text-xs"
            style={{
              borderColor: `${RECURRENCE_COLORS.custom}40`,
              backgroundColor: `${RECURRENCE_COLORS.custom}10`,
              color: RECURRENCE_COLORS.custom,
            }}
          >
            <strong>Summary:</strong> {recurrenceLabel(rule) || "—"}
            {rule.end?.type === "count" && ` · ends after ${rule.end.count} times`}
            {rule.end?.type === "until" && rule.end.until && ` · ends ${rule.end.until}`}
          </div>
        </div>

        <DialogFooter className="px-6 pb-5 gap-2">
          <Button type="button" variant="outline" onClick={onClose} data-testid="custom-cancel-btn">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onSave(rule)}
            data-testid="custom-save-btn"
            style={{ backgroundColor: RECURRENCE_COLORS.custom, color: "#0a0a0a" }}
            className="hover:opacity-90"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
